"use client";

import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/buttons/toggle-group";
import { Button } from "@/shared/ui/buttons/button";
import { cn } from "@/shared/lib/utils";
import {
  BOARD_SEGMENTS,
  EMPTY_FILTERS,
  LFG_RANKS,
  mergeHourSlots,
  withFilters,
  type BoardFilters,
} from "../model/board-filters";

const pad = (h: number) => String(h).padStart(2, "0");

/** Выбранное значение в акценте: одинаково у сегмента, сортировки и пилюль. */
const HIGHLIGHT_ON = "data-[state=on]:bg-highlight-soft data-[state=on]:text-highlight";

interface Pill {
  key: string;
  label: string;
  remove: () => BoardFilters;
}

function usePills(f: BoardFilters): Pill[] {
  const t = useTranslations("LfgBoard");
  const pills: Pill[] = f.roles.map((r) => ({
    key: `role-${r}`,
    label: t(`role.${r}`),
    remove: () => withFilters(f, { roles: f.roles.filter((x) => x !== r) }),
  }));
  if (f.rank) {
    const from = t(`rank.${LFG_RANKS[f.rank.min]}`);
    const to = t(`rank.${LFG_RANKS[f.rank.max]}`);
    pills.push({ key: "rank", label: from === to ? from : t("rankRange", { from, to }), remove: () => withFilters(f, { rank: null }) });
  }
  if (f.mic !== "any") pills.push({ key: "mic", label: t(`mic.${f.mic}`), remove: () => withFilters(f, { mic: "any" }) });
  if (f.hourSlots.length) {
    const ranges = mergeHourSlots(f.hourSlots);
    pills.push({
      key: "hours",
      label: ranges ? ranges.map(([a, b]) => t("hoursRange", { from: pad(a), to: pad(b) })).join(", ") : t("allDay"),
      remove: () => withFilters(f, { hourSlots: [] }),
    });
  }
  for (const x of f.formats)
    pills.push({ key: `format-${x}`, label: t(`format.${x}`), remove: () => withFilters(f, { formats: f.formats.filter((y) => y !== x) }) });
  for (const s of f.servers)
    pills.push({ key: `server-${s}`, label: s, remove: () => withFilters(f, { servers: f.servers.filter((y) => y !== s) }) });
  for (const l of f.languages)
    pills.push({ key: `lang-${l}`, label: t(`language.${l}`), remove: () => withFilters(f, { languages: f.languages.filter((y) => y !== l) }) });
  return pills;
}

interface BoardToolbarProps {
  filters: BoardFilters;
  onChange: (next: BoardFilters) => void;
  /** undefined — список ещё грузится или не загрузился: счётчик не врёт нулём. */
  total: number | undefined;
}

export function BoardToolbar({ filters, onChange, total }: BoardToolbarProps) {
  const t = useTranslations("LfgBoard");
  const pills = usePills(filters);

  return (
    <div className="flex flex-col gap-4">
      <ToggleGroup
        type="single"
        value={filters.segment}
        // Radix отдаёт "" при повторном клике по выбранному — сегмент не может быть пустым.
        onValueChange={(v) => {
          const next = BOARD_SEGMENTS.find((s) => s === v);
          if (next) onChange(withFilters(filters, { segment: next }));
        }}
        aria-label={t("segmentLabel")}
        className="w-full gap-1.5 rounded-xl border border-border bg-muted/30 p-1.5"
      >
        {BOARD_SEGMENTS.map((s) => (
          <ToggleGroupItem
            key={s}
            value={s}
            className={cn("h-9 flex-1 rounded-lg! text-sm font-bold text-muted-foreground", HIGHLIGHT_ON)}
          >
            {t(`segment.${s}`)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {pills.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {pills.map((p) => (
            <li key={p.key}>
              <button
                type="button"
                onClick={() => onChange(p.remove())}
                aria-label={t("removeFilter", { label: p.label })}
                className="inline-flex h-7 items-center gap-1.5 rounded-full border border-highlight-border bg-highlight-soft px-2.5 text-xs font-semibold text-highlight outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                {p.label}
                <X aria-hidden className="size-3 opacity-70" />
              </button>
            </li>
          ))}
          <li>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 rounded-full px-2.5 text-xs text-muted-foreground"
              onClick={() => onChange({ ...EMPTY_FILTERS, segment: filters.segment, sort: filters.sort })}
            >
              {t("resetAll")}
            </Button>
          </li>
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {total !== undefined &&
            t.rich("found", { count: total, b: (chunks) => <span className="font-bold text-highlight">{chunks}</span> })}
        </p>
      </div>
    </div>
  );
}
